import { FC, useRef, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { differenceInDays, parseISO } from 'date-fns';
import { VariantProps } from 'class-variance-authority';
import { Root } from '@radix-ui/react-dialog';

import { route_path } from '@app/utils/route_path';

import { useLogOutMutation } from '@app/store/api/user.api';

import { BxGridAltIcon, CrossIcon, LogoImage, WorldIcon } from '@app/components/ui/icons';
import { Container } from '@app/shared/ui/container';
import { Button } from '@app/shared/ui/button';
import { cvaIcon } from '@app/shared/ui/elements';
import { Skeleton } from '@app/shared/ui/skeleton';
import { BurgerButton } from '@app/shared/ui/burger-button';
import { cn } from '@app/app/lib';
import { useAppSelector } from '@app/hooks/use-store';
import type { CommonOutletContext } from '@app/app/layouts';

import { AccountHeaderPopover } from '@app/widgets/header/ui/modals/account-header-popover';

import { useMediaQuery } from 'usehooks-ts';

import { useTranslation, useLanguages, useLocalizedNavigate } from '@app/app/i18n';
import { LocalizedLink, LocalizedNavLink } from '@app/shared/ui/localized-link';

import { isBoxedMode } from '@app/utils/user-status.utils';

import { useHeaderStore } from '../model/header.store';
import { useNavLinks } from '../model/static-data';

import { screens } from '../../../../../../config/tailwind';

import { MobileMenu } from './mobile-menu';
import { LanguageSwitcher } from './components/language-switcher';

import {
  cvaActionsMenu,
  cvaBurgerButton,
  cvaContainer,
  cvaHeaderMainContent,
  cvaHeaderNavMenu,
  cvaLogo,
  cvaLogoLink,
  cvaMainHeader,
  cvaMenuItem,
  cvaRoot,
} from './header-styles';

type HeaderProps = VariantProps<typeof cvaRoot>;

export const Header: FC<HeaderProps> = (props) => {
  const { t, currentLanguage } = useTranslation('header');
  const languages = useLanguages('header');
  const { isAuthenticated, isFirstAuthCheck } = useAppSelector((state) => state.authState);
  const { isTransparent } = props;

  const { context, user_ctx, commonContext } = useOutletContext<CommonOutletContext>();
  const isMobile = useMediaQuery(`(max-width: ${screens.screens['semiMd-max'].max})`);

  const isBoxed = isBoxedMode(commonContext);
  const isUserIdEven = user_ctx?.id !== undefined; // && user_ctx.id % 2 === 0;
  const isWorkspaceDefault = isBoxed || isUserIdEven;

  const navLinks = useNavLinks();
  // const navLinksV2 = useNavLinksV2();

  const currentLang = languages.find((lang) => lang.code === currentLanguage) || languages[0];

  const headerRef = useRef<HTMLDivElement>(null);
  const navigate = useLocalizedNavigate();
  const [logoutQuery] = useLogOutMutation();

  const { isMenuOpen, toggleHeaderMenuOpen } = useHeaderStore();
  useEffect(() => {
    if (isMenuOpen) {
      window.scrollTo(0, 0);
    }
  }, [isMenuOpen]);

  const isTransparentStyles = !isMenuOpen && !!isTransparent;

  const handleClose = () => toggleHeaderMenuOpen(false);
  const handleToggleMenuOpen = () => toggleHeaderMenuOpen(!isMenuOpen);

  const logOutHandler = () => {
    logoutQuery().then(() => {
      handleClose();
      navigate(route_path.main);
    });
  };

  const daysLeft =
    context?.product_balance &&
    context?.product_balance.is_active &&
    context?.product_balance.product.is_trial &&
    context?.product_balance.expire
      ? differenceInDays(parseISO(context?.product_balance.expire), new Date())
      : null;

  return (
    <Root open={isMenuOpen}>
      {isMobile && (
        <MobileMenu daysLeft={daysLeft} onClose={handleClose} onLogOut={logOutHandler} />
      )}
      {/* TODO: Replace font-inter */}
      <header className={cn(cvaRoot({ isTransparent: isTransparentStyles }), 'font-inter')}>
        <div ref={headerRef} className={cvaMainHeader()}>
          <Container className={cvaContainer()}>
            {/* Logo */}
            <LocalizedLink
              to={
                isAuthenticated
                  ? isWorkspaceDefault
                    ? route_path.workspaceCreate
                    : route_path.create
                  : '/'
              }
              className={cvaLogoLink()}
            >
              {isBoxed ? (
                <span className={cvaLogo()}>Sibur Roboslide</span>
              ) : (
                <LogoImage className={cvaLogo()} />
              )}
            </LocalizedLink>

            {/* Header main content*/}
            <div className={cvaHeaderMainContent()}>
              {/* Navigations - скрыты в boxed режиме */}
              {!isBoxed && (
                <nav className={cvaHeaderNavMenu()}>
                  {navLinks.map(({ to, title, id }) => {
                    // Templates - статическая страница, используем обычную ссылку
                    if (to === route_path.templates) {
                      return (
                        <a
                          key={id}
                          href="/templates/"
                          className={cvaMenuItem({ isTransparent: isTransparentStyles })}
                        >
                          {title}
                        </a>
                      );
                    }
                    return (
                      <LocalizedNavLink
                        key={id}
                        className={({ isActive }) =>
                          cvaMenuItem({ isActive, isTransparent: isTransparentStyles })
                        }
                        to={to}
                      >
                        {title}
                      </LocalizedNavLink>
                    );
                  })}
                  <a
                    href={route_path.blog}
                    className={cvaMenuItem({ isTransparent: isTransparentStyles })}
                  >
                    {t('links.blog')}
                  </a>
                </nav>
              )}

              {/* Header actions */}
              <div className={cvaActionsMenu()}>
                {isFirstAuthCheck ? (
                  <>
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <Skeleton className="h-10 w-24 rounded-full" />
                    <Skeleton className="h-10 w-20 rounded-lg" />
                  </>
                ) : !isAuthenticated ? (
                  <>
                    <LanguageSwitcher
                      side="bottom"
                      trigger={
                        <button
                          type="button"
                          className={cvaMenuItem({
                            isDotHidden: true,
                            isTransparent: isTransparentStyles,
                          })}
                        >
                          <WorldIcon className={cvaIcon()} />
                          <span>{currentLang.flag}</span>
                        </button>
                      }
                    />
                    <Button borderRadius="3xl" asChild>
                      <LocalizedLink to="/register/">{t('auth.signup')}</LocalizedLink>
                    </Button>

                    <LocalizedLink
                      to="/login/"
                      className={cvaMenuItem({
                        isDotHidden: true,
                        isTransparent: isTransparentStyles,
                      })}
                    >
                      {t('auth.login')}
                    </LocalizedLink>
                  </>
                ) : (
                  <>
                    {/* Create new button */}
                    <Button
                      variant={isTransparentStyles ? 'primary' : 'outline'}
                      borderRadius="3xl"
                      asChild
                    >
                      <LocalizedLink
                        to={isWorkspaceDefault ? route_path.workspaceCreate : route_path.create}
                      >
                        {t('auth.create')}
                      </LocalizedLink>
                    </Button>

                    {/* My projects*/}
                    <LocalizedNavLink
                      to="/projects/"
                      className={({ isActive }) =>
                        cvaMenuItem({
                          isActive,
                          isTransparent: isTransparentStyles,
                          isDotHidden: true,
                        })
                      }
                    >
                      <BxGridAltIcon className={cvaIcon()} />
                      <span>{t('account.myPresentations')}</span>
                    </LocalizedNavLink>

                    <AccountHeaderPopover
                      daysLeft={daysLeft}
                      isTransparentStyles={isTransparentStyles}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Language switcher for mobile unauthorized users */}
            {isMobile && !isAuthenticated && !isFirstAuthCheck && (
              <div className="ml-auto mr-4">
                <LanguageSwitcher
                  side="bottom"
                  trigger={
                    <button
                      type="button"
                      className={cvaMenuItem({
                        isDotHidden: true,
                        isTransparent: isTransparentStyles,
                      })}
                    >
                      <WorldIcon className={cvaIcon()} />
                      <span>{currentLang.flag}</span>
                    </button>
                  }
                />
              </div>
            )}

            {/* Burger button */}
            <BurgerButton
              isOpen={isMenuOpen}
              activeIcon={CrossIcon}
              isTransparent={isTransparentStyles}
              onClick={handleToggleMenuOpen}
              className={cn(
                cvaBurgerButton(),
                !(isMobile && !isAuthenticated && !isFirstAuthCheck) && 'ml-auto',
              )}
              ariaLabel={isMenuOpen ? t('menu.close') : t('menu.open')}
            />
          </Container>
        </div>
      </header>
    </Root>
  );
};
